import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { HashRouter as Router, Routes, Route } from 'react-router-dom';
import DemoEntry from './components/DemoEntry';
import ModuleSelection from './components/ModuleSelection';
import DemoBanner from './components/DemoBanner';
import SetupWizard from '../shared/components/setup/SetupWizard';
import WelcomeLoginPage from '../shared/components/setup/WelcomeLoginPage';
import { AppContent } from '../AppContent';
import demoTrackingService from './services/demoTrackingService';
import { applyCloudDemoLicense } from './applyCloudDemoLicense';

const LS_USER = 'qawaem_demo_user';
const LS_MODULES = 'qawaem_demo_modules';

/**
 * Flux Cloudflare : formulaire → choix modules → ERP (src/) avec bannière démo.
 */
export default function OnlineDemoLeadGate() {
  const { t } = useTranslation();
  const [demoState, setDemoState] = useState({
    hasEnteredInfo: false,
    userData: null,
    selectedModules: null,
    isLoading: true,
  });

  useEffect(() => {
    const storedUser = localStorage.getItem(LS_USER);
    const storedModules = localStorage.getItem(LS_MODULES);

    if (storedUser) {
      try {
        const userData = JSON.parse(storedUser);
        const modules = storedModules ? JSON.parse(storedModules) : null;
        if (Array.isArray(modules) && modules.length > 0) {
          applyCloudDemoLicense(modules);
        }
        setDemoState({
          hasEnteredInfo: true,
          userData,
          selectedModules: modules,
          isLoading: false,
        });
      } catch (e) {
        console.error('Online demo: invalid stored state', e);
        setDemoState((p) => ({ ...p, isLoading: false }));
      }
    } else {
      setDemoState((p) => ({ ...p, isLoading: false }));
    }
  }, []);

  const handleUserSubmit = (userData) => {
    setDemoState((prev) => ({
      ...prev,
      hasEnteredInfo: true,
      userData,
    }));
  };

  const handleModuleSelect = async (selectedModules) => {
    applyCloudDemoLicense(selectedModules);

    setDemoState((prev) => ({
      ...prev,
      selectedModules,
    }));

    const u = demoState.userData;
    if (u) {
      await demoTrackingService.startSession(
        u.name,
        u.phone,
        u.company,
        t('onlineDemo.packageLabel.modules_tracking', { count: selectedModules.length })
      );
    }
  };

  const handleQuit = async () => {
    await demoTrackingService.endSession();
    localStorage.removeItem(LS_MODULES);
    window.location.reload();
  };

  useEffect(() => {
    const onUnload = () => {
      if (demoState.selectedModules?.length) {
        demoTrackingService.endSession();
      }
    };
    window.addEventListener('beforeunload', onUnload);
    return () => window.removeEventListener('beforeunload', onUnload);
  }, [demoState.selectedModules]);

  if (demoState.isLoading) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
          background: '#0F172A',
          color: '#F1F5F9',
        }}
      >
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>⏳</div>
          <div>{t('onlineDemo.loading_demo')}</div>
        </div>
      </div>
    );
  }

  if (!demoState.hasEnteredInfo) {
    return <DemoEntry onSubmit={handleUserSubmit} />;
  }

  if (!demoState.selectedModules || demoState.selectedModules.length === 0) {
    return (
      <ModuleSelection
        userData={demoState.userData}
        onSelect={handleModuleSelect}
      />
    );
  }

  const modCount = demoState.selectedModules.length;
  const packageLabel = t(
    modCount === 1
      ? 'onlineDemo.packageLabel.selected_one'
      : 'onlineDemo.packageLabel.selected_other',
    { count: modCount }
  );

  return (
    <Router>
      <div
        style={{
          height: '100dvh',
          maxHeight: '100dvh',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        <DemoBanner packageName={packageLabel} onQuit={handleQuit} />
        <div
          style={{
            flex: 1,
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}
        >
          <Routes>
            <Route path="/setup" element={<SetupWizard />} />
            <Route path="/login" element={<WelcomeLoginPage />} />
            <Route path="/*" element={<AppContent />} />
          </Routes>
        </div>
      </div>
    </Router>
  );
}
